/**
 * Network Retry Utility
 *
 * Universal retry logic with exponential backoff for network failures.
 * Automatically retries transient errors, shows user-friendly toasts.
 */

import { toast } from './toast';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:util:network-retry');

export type RetryOptions = {
  maxRetries?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  shouldRetry?: (error: unknown) => boolean;
  onRetry?: (attempt: number, delayMs: number) => void;
};

const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 10000,
  shouldRetry: isRetryableError,
  onRetry: () => {},
};

/**
 * Check if an error is retryable (network/transient issues)
 */
export function isRetryableError(error: unknown): boolean {
  if (!error) return false;

  // Check for network errors
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    const networkKeywords = [
      'network',
      'fetch',
      'timeout',
      'etimedout',
      'econnrefused',
      'enotfound',
      'connection',
      'socket',
      'abort',
    ];

    if (networkKeywords.some(keyword => message.includes(keyword))) {
      return true;
    }
  }

  // Check for HTTP status codes that are retryable
  if (typeof error === 'object' && error !== null) {
    const err = error as any;

    // Transient HTTP errors
    if ('status' in err) {
      const status = err.status;
      // Retry on: 408 (Timeout), 429 (Rate Limit), 500-599 (Server Errors)
      return status === 408 || status === 429 || (status >= 500 && status < 600);
    }
  }

  return false;
}

/**
 * Calculate exponential backoff delay
 */
function calculateDelay(attempt: number, initialDelayMs: number, maxDelayMs: number): number {
  const exponentialDelay = initialDelayMs * Math.pow(2, attempt);
  // Add jitter to prevent thundering herd
  const jitter = Math.random() * 0.3 * exponentialDelay;
  return Math.min(exponentialDelay + jitter, maxDelayMs);
}

/**
 * Sleep utility
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Execute an async operation with automatic retry on network failures
 *
 * @example
 * const result = await withRetry(
 * () => api.fetchData(),
 * { maxRetries: 3 }
 * );
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  let lastError: any;

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      // Execute the operation
      const result = await operation();

      // Success! If this was a retry, show success toast
      if (attempt > 0) {
        toast.success('Connection Restored', 'Your request was completed successfully.');
      }

      return result;
    } catch (error) {
      lastError = error;

      // Check if we should retry this error
      if (!opts.shouldRetry(error)) {
        logger.info(`[RETRY] Error is not retryable, giving up:`, error);
        throw error;
      }

      // If this was the last attempt, throw
      if (attempt >= opts.maxRetries) {
        logger.error(`[RETRY] Max retries (${opts.maxRetries}) exceeded, giving up:`, error);
        throw error;
      }

      // Calculate delay for next retry
      const delayMs = calculateDelay(attempt, opts.initialDelayMs, opts.maxDelayMs);

      logger.warn(
        `[RETRY] Attempt ${attempt + 1}/${opts.maxRetries + 1} failed, retrying in ${Math.round(delayMs / 1000)}s...`,
        error
      );

      // Show user-friendly toast
      toast.info(
        'Connection Issue',
        `Retrying in ${Math.round(delayMs / 1000)} second${Math.round(delayMs / 1000) !== 1 ? 's' : ''}...`
      );

      // Notify caller
      opts.onRetry(attempt + 1, delayMs);

      // Wait before retrying
      await sleep(delayMs);
    }
  }

  // This shouldn't be reached, but TypeScript needs it
  throw lastError;
}

/**
 * Wrap an async function to automatically retry on network failures
 *
 * @example
 * const reliableFetch = retryable(fetchData, { maxRetries: 3 });
 * const result = await reliableFetch(params);
 */
export function retryable<TArgs extends any[], TReturn>(
  fn: (...args: TArgs) => Promise<TReturn>,
  options: RetryOptions = {}
): (...args: TArgs) => Promise<TReturn> {
  return async (...args: TArgs) => {
    return withRetry(() => fn(...args), options);
  };
}

/**
 * Check if current error is a network error (for inline error handling)
 */
export function isNetworkError(error: unknown): boolean {
  return isRetryableError(error);
}

/**
 * Get user-friendly error message for network errors
 */
export function getNetworkErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    if (message.includes('timeout') || message.includes('etimedout')) {
      return 'Request timed out. Please check your connection and try again.';
    }

    if (message.includes('econnrefused') || message.includes('enotfound')) {
      return 'Unable to reach the server. Please check your internet connection.';
    }

    if (message.includes('abort')) {
      return 'Request was cancelled. Please try again.';
    }
  }

  return 'Network error occurred. Please check your connection and try again.';
}
